/**
 * patterns.tsx — 设计系统活文档「设计模式 · 全局规则」+「设计原则」分区
 *
 * 移植自原型 design/Novel-admin/src/pages/design-system-patterns.jsx
 * （反馈 / 导航 / 录入 / 展示 / 格式 / 文案 / 按钮 / 列表 +
 *  极致密度 / 数字一律 mono / i18n / 状态色三层 / fallback 兜底链）。
 * 原型用 window.__dsPatternSections 注册；这里改为具名 ESM export。
 * PrincipleCard 移到 ds-primitives 共享；window.Icons → import { Icons }。
 */

import { Icons } from '~/lib/icons';
import { Table } from '~/ui/Table';
import { DSBlock, DSHeader, DSRule, PrincipleCard } from './ds-primitives';

// ============================================================
// 模式 · 反馈
// ============================================================
export function DSPatternFeedback() {
  return (
    <>
      <DSHeader title="反馈 · Feedback" sub="四种反馈渠道，按操作分量选择" />
      <DSBlock title="反馈分量四级">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 10,
          }}
        >
          {[
            { l: 'Toast', w: '轻', t: '保存 / 删除单项 / 复制 ID', sec: '2-3 秒' },
            { l: 'Alert', w: '中', t: '全局公告 / 模式说明 / 帮助提示', sec: '常驻' },
            { l: 'Dialog', w: '重', t: '二次确认 / 编辑 / 复杂表单', sec: '需关闭' },
            { l: 'Page', w: '极重', t: '事故 / 维护 / 权限', sec: '需跳转' },
          ].map((c, i) => (
            <div key={c.l} className="card" style={{ padding: 14 }}>
              <div className="hstack" style={{ gap: 6, marginBottom: 4 }}>
                <span className="mono" style={{ fontSize: 10, color: 'var(--accent)' }}>
                  0{i + 1}
                </span>
                <span style={{ fontWeight: 500, fontSize: 13 }}>{c.l}</span>
                <span className="chip" style={{ fontSize: 9, padding: '0 5px' }}>
                  {c.w}
                </span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{c.t}</div>
              <div style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginTop: 3 }}>{c.sec}</div>
            </div>
          ))}
        </div>
      </DSBlock>
      <DSRule tone="do">破坏性 / 不可逆操作必须走 Dialog，不能只 Toast</DSRule>
      <DSRule tone="do">短时态变化用 Toast；长期状态用 Alert 横幅</DSRule>
      <DSRule tone="dont">不要叠 Toast — 同时最多 1 个</DSRule>
    </>
  );
}

// ============================================================
// 模式 · 导航
// ============================================================
export function DSPatternNav() {
  return (
    <>
      <DSHeader
        title="导航 · Navigation"
        sub="三层导航：Sidebar (主模块) → 顶部 (App 切换) → 页面内 Tab"
      />
      <DSBlock title="层级">
        <div className="card-flat" style={{ padding: 14 }}>
          <div className="vstack" style={{ gap: 8 }}>
            {[
              { l: 1, name: 'Sidebar 左侧导航', v: '主模块入口 · 1 级，最多 8 个一级项' },
              { l: 2, name: 'App Switcher 顶部', v: '全部 App / 单个 App 切换 · 全局' },
              { l: 3, name: '页面内 Tab', v: '详情页 / 设置页内分页 · 4-7 个' },
              { l: 4, name: '面包屑', v: '深层详情页回退 · 仅显示一层「返回」' },
            ].map((n) => (
              <div key={n.l} className="hstack" style={{ gap: 12 }}>
                <span
                  className="mono"
                  style={{ fontSize: 10, color: 'var(--fg-faint)', width: 30 }}
                >
                  L{n.l}
                </span>
                <span style={{ fontWeight: 500, fontSize: 12.5 }}>{n.name}</span>
                <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{n.v}</span>
              </div>
            ))}
          </div>
        </div>
      </DSBlock>
      <DSRule tone="do">所有详情页提供「返回 XX 列表」面包屑，配合 dirty 检测做二次确认</DSRule>
      <DSRule tone="dont">不做多级展开折叠 — 信息密度优先</DSRule>
    </>
  );
}

// ============================================================
// 模式 · 数据录入
// ============================================================
export function DSPatternInput() {
  return (
    <>
      <DSHeader title="数据录入 · Input" sub="表单优先：FormRow + 多语言字段统一模式" />
      <DSBlock title="组件选择">
        <Table className="tbl">
          <thead>
            <tr>
              <th>场景</th>
              <th>组件</th>
              <th>备注</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>短文本</td>
              <td>
                <code>{`<input className="input"/>`}</code>
              </td>
              <td>书名、邮箱、URL</td>
            </tr>
            <tr>
              <td>长文本</td>
              <td>
                <code>{`<textarea className="input"/>`}</code>
              </td>
              <td>简介、反馈回复</td>
            </tr>
            <tr>
              <td>数字 / ID</td>
              <td>
                <code>{`<input className="input mono"/>`}</code>
              </td>
              <td>必加 mono</td>
            </tr>
            <tr>
              <td>枚举 2-3 项</td>
              <td>
                <code>{`<div className="seg">`}</code>
              </td>
              <td>Segmented</td>
            </tr>
            <tr>
              <td>枚举 4+ 项</td>
              <td>
                <code>{`<select className="input"/>`}</code>
              </td>
              <td />
            </tr>
            <tr>
              <td>开关</td>
              <td>
                <code>Switch</code>
              </td>
              <td />
            </tr>
            <tr>
              <td>多语言文案</td>
              <td>
                <code>MultiLangFormRows</code>
              </td>
              <td>核心模式</td>
            </tr>
            <tr>
              <td>多选 / 标签</td>
              <td>FilterButton</td>
              <td>列表筛选</td>
            </tr>
            <tr>
              <td>日期</td>
              <td>DatePicker</td>
              <td />
            </tr>
          </tbody>
        </Table>
      </DSBlock>
    </>
  );
}

// ============================================================
// 模式 · 数据展示
// ============================================================
export function DSPatternDisplay() {
  return (
    <>
      <DSHeader title="数据展示 · Display" sub="表格 / 卡片 / 图表 / Stat — 按数据形态选择" />
      <DSBlock title="选择决策树">
        <ol
          style={{
            fontSize: 12.5,
            color: 'var(--fg-muted)',
            lineHeight: 1.8,
            paddingLeft: 18,
            margin: 0,
          }}
        >
          <li>
            单个数字 / KPI → <b style={{ color: 'var(--fg)' }}>Stat 卡片</b>
          </li>
          <li>
            少量带封面 / 头像的项目 → <b style={{ color: 'var(--fg)' }}>Card 网格</b>
          </li>
          <li>
            多字段、需要对齐扫读 → <b style={{ color: 'var(--fg)' }}>Table</b>
          </li>
          <li>
            时间序列 → <b style={{ color: 'var(--fg)' }}>折线 / 面积图</b>
          </li>
          <li>
            构成比例 → <b style={{ color: 'var(--fg)' }}>堆叠柱 / 饼</b>
          </li>
          <li>
            分布 → <b style={{ color: 'var(--fg)' }}>直方图 / 热力图</b>
          </li>
          <li>
            转化漏斗 → <b style={{ color: 'var(--fg)' }}>Funnel 步骤</b>
          </li>
        </ol>
      </DSBlock>
      <DSRule tone="do">空状态优先用插画 + 一句话 + 主操作按钮；不要光秃秃放「暂无数据」</DSRule>
      <DSRule tone="dont">单页 KPI 卡片不要超过 8 个 — 超过就拆 Tab</DSRule>
    </>
  );
}

// ============================================================
// 模式 · 数据格式
// ============================================================
export function DSPatternFormat() {
  return (
    <>
      <DSHeader title="数据格式 · Format" sub="数字 / 日期 / 货币 / 国家 / 语言的展示约定" />
      <DSBlock title="格式约定">
        <Table className="tbl">
          <thead>
            <tr>
              <th>类型</th>
              <th>格式</th>
              <th>样例</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>大数字</td>
              <td>千分位 + 紧凑（K/M/B）</td>
              <td className="mono">12,456 · 1.14M · €23B</td>
            </tr>
            <tr>
              <td>百分比</td>
              <td>1 位小数 + %</td>
              <td className="mono">12.4%</td>
            </tr>
            <tr>
              <td>金额</td>
              <td>欧元 € + 千分位 + 2 位小数</td>
              <td className="mono">€128.40</td>
            </tr>
            <tr>
              <td>日期</td>
              <td>YYYY-MM-DD · 短文本「2 小时前」</td>
              <td className="mono">2026-05-28</td>
            </tr>
            <tr>
              <td>时间</td>
              <td>HH:mm（24h）· 不带秒</td>
              <td className="mono">09:41</td>
            </tr>
            <tr>
              <td>ID / hash</td>
              <td>mono 字体 · 不变形</td>
              <td className="mono">bk_29481</td>
            </tr>
            <tr>
              <td>国家</td>
              <td>大写 ISO 代码 + 空格 + 中文</td>
              <td>DE 德国 · FR 法国</td>
            </tr>
            <tr>
              <td>语言</td>
              <td>大写代码 + 空格 + 中文</td>
              <td>EN 英语 · DE 德语</td>
            </tr>
            <tr>
              <td>用户名</td>
              <td>原文 · 不大写化</td>
              <td>mateor</td>
            </tr>
          </tbody>
        </Table>
      </DSBlock>
      <DSRule tone="do">
        国家用 <code>fmtCountry(code)</code> · 语言用 <code>fmtLang(code)</code>
      </DSRule>
      <DSRule tone="dont">不要用原生名（Deutsch / Français）；不要混用 ISO-2 / ISO-3</DSRule>
    </>
  );
}

// ============================================================
// 模式 · 文案
// ============================================================
export function DSPatternCopy() {
  return (
    <>
      <DSHeader title="文案 · Copy" sub="后台文案以中文为主 · 简洁、明确、无废话" />
      <DSBlock title="语气">
        <ul
          style={{
            fontSize: 12.5,
            color: 'var(--fg-muted)',
            lineHeight: 1.8,
            paddingLeft: 18,
            margin: 0,
          }}
        >
          <li>动词开头，主语省略：「保存」「下架」「导出报表」</li>
          <li>禁用感叹号 / 客套：不写「请」「您」「亲」</li>
          <li>中英混排：英文 / 数字与中文之间空一格</li>
          <li>缩写：第一次出现写全称 + 缩写「订阅 BONUS」</li>
        </ul>
      </DSBlock>
      <DSBlock title="术语表（部分）">
        <Table className="tbl">
          <thead>
            <tr>
              <th>中文</th>
              <th>英文</th>
              <th>用途</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>App</td>
              <td>App</td>
              <td>每个独立产品（Rivmoon / Aurora / Lumen…）</td>
            </tr>
            <tr>
              <td>书籍</td>
              <td>Book / Novel</td>
              <td>内容主体</td>
            </tr>
            <tr>
              <td>章节</td>
              <td>Chapter</td>
              <td />
            </tr>
            <tr>
              <td>上架 / 隐藏 / 下架</td>
              <td>Live / Hidden / Off</td>
              <td>书籍状态机</td>
            </tr>
            <tr>
              <td>千字单价</td>
              <td>cost-per-kw</td>
              <td>计价单位</td>
            </tr>
            <tr>
              <td>COIN</td>
              <td>COIN</td>
              <td>站内虚拟币（不译）</td>
            </tr>
            <tr>
              <td>BONUS</td>
              <td>BONUS</td>
              <td>赠送 COIN（不译）</td>
            </tr>
            <tr>
              <td>反馈</td>
              <td>Feedback</td>
              <td>用户工单</td>
            </tr>
            <tr>
              <td>允许语言集合</td>
              <td>allowed_languages</td>
              <td>App 配置项</td>
            </tr>
          </tbody>
        </Table>
      </DSBlock>
    </>
  );
}

// ============================================================
// 模式 · 按钮
// ============================================================
export function DSPatternButtons() {
  return (
    <>
      <DSHeader title="按钮 · Action" sub="按钮的位置、文案、风格约定" />
      <DSBlock title="位置">
        <ul
          style={{
            fontSize: 12.5,
            color: 'var(--fg-muted)',
            lineHeight: 1.8,
            paddingLeft: 18,
            margin: 0,
          }}
        >
          <li>
            <b style={{ color: 'var(--fg)' }}>页面右上角</b>：主操作（新建 / 导出），accent 或
            default
          </li>
          <li>
            <b style={{ color: 'var(--fg)' }}>列表行右侧</b>：单条操作，sm / ghost
          </li>
          <li>
            <b style={{ color: 'var(--fg)' }}>列表行 ⋯</b>：次要 / 危险操作走 ContextMenu
          </li>
          <li>
            <b style={{ color: 'var(--fg)' }}>Dialog 底部</b>：左 cancel，右 confirm；危险 confirm
            红
          </li>
          <li>
            <b style={{ color: 'var(--fg)' }}>详情页底部</b>：只在 dirty 时出现「保存 / 放弃」
          </li>
        </ul>
      </DSBlock>
      <DSBlock title="文案">
        <ul
          style={{
            fontSize: 12.5,
            color: 'var(--fg-muted)',
            lineHeight: 1.8,
            paddingLeft: 18,
            margin: 0,
          }}
        >
          <li>不写「确定 / 提交」— 用具体动词：「保存」「下架」「发放」</li>
          <li>危险操作写明对象：「删除区块」而非「删除」</li>
          <li>2-4 字最佳，超过 6 字时考虑拆按钮或用菜单</li>
        </ul>
      </DSBlock>
    </>
  );
}

// ============================================================
// 模式 · 数据列表
// ============================================================
export function DSPatternLists() {
  return (
    <>
      <DSHeader
        title="数据列表 · List"
        sub="所有列表三件套：SearchInput + FilterButton 们 + 视图切换"
      />
      <DSBlock title="结构">
        <div className="card-flat" style={{ padding: 14 }}>
          <div className="hstack" style={{ gap: 8, marginBottom: 10 }}>
            <div style={{ position: 'relative', width: 240 }}>
              <Icons.IconSearch
                size={11}
                style={{ position: 'absolute', left: 8, top: 7, color: 'var(--fg-faint)' }}
              />
              <input
                className="input"
                placeholder="搜索…"
                style={{ height: 26, paddingLeft: 26, fontSize: 11.5 }}
              />
            </div>
            <button type="button" className="btn btn-sm">
              <Icons.IconFilter size={10} />
              分类
              <Icons.IconChevDown size={10} />
            </button>
            <button type="button" className="btn btn-sm">
              <Icons.IconFilter size={10} />
              语言
              <Icons.IconChevDown size={10} />
            </button>
            <button type="button" className="btn btn-sm">
              <Icons.IconFilter size={10} />
              状态
              <Icons.IconChevDown size={10} />
            </button>
            <div style={{ flex: 1 }} />
            <div className="seg">
              <button type="button" className="on">
                <Icons.IconLayout size={10} />
              </button>
              <button type="button">
                <Icons.IconList size={10} />
              </button>
            </div>
            <button type="button" className="btn btn-sm">
              <Icons.IconDownload size={10} />
              导出
            </button>
          </div>
          <div
            style={{
              height: 100,
              background: 'var(--bg-subtle)',
              borderRadius: 5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--fg-faint)',
              fontSize: 11,
            }}
          >
            列表内容
          </div>
          <div className="hstack" style={{ marginTop: 10, justifyContent: 'flex-end', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>共 248 条</span>
            <button type="button" className="btn btn-sm">
              ‹
            </button>
            <span className="mono" style={{ fontSize: 11 }}>
              1 / 12
            </span>
            <button type="button" className="btn btn-sm">
              ›
            </button>
          </div>
        </div>
      </DSBlock>
      <DSRule tone="do">筛选条件改变后 page 重置到 1</DSRule>
      <DSRule tone="do">所有列表支持导出（按当前筛选）</DSRule>
    </>
  );
}

// ============================================================
// 设计原则
// ============================================================
export function DSPDensity() {
  return (
    <>
      <DSHeader title="极致密度" sub="后台工具用户每天看几小时，每屏多放 30% 信息就少滚动 30%" />
      <PrincipleCard
        name="字号尽量小"
        desc="正文 12.5–13px，副文 11–11.5px，超小标签 10–10.5px。不为可读性牺牲密度。"
      />
      <PrincipleCard
        name="行高紧凑"
        desc="line-height 1.45 (正文) / 1.55 (段落)；列表行高 row-h 28/32/40 三档可切"
      />
      <PrincipleCard
        name="无装饰留白"
        desc="卡片间 gap 8-14px；卡内 padding 10-16px；不留视觉留白"
      />
      <DSRule tone="dont">不为了"呼吸感"加 20+px 留白 — 信息密度优先</DSRule>
    </>
  );
}

export function DSPMono() {
  return (
    <>
      <DSHeader title="数字一律 mono" sub="所有数字、ID、hash、邮箱、时间、代码 — 单一字体规则" />
      <PrincipleCard
        name="为什么"
        desc="Geist Mono 是 tabular，所有数字等宽。表格里数字竖向自动对齐，扫读速度提升 2x。"
      />
      <PrincipleCard
        name="哪些场景必加 mono"
        examples={[
          '所有数字（金额、计数、百分比）',
          'ID / hash / token / 订单号',
          '邮箱地址',
          '时间戳 / 日期',
          '国家代码 / 语言代码 (DE / EN)',
          '代码片段',
        ]}
        desc={`加 className="mono" 即可；表格数字列加 className="num"`}
      />
      <DSRule tone="do">数字一律 mono · ID 一律 mono · 代码一律 mono</DSRule>
      <DSRule tone="dont">中文 / 段落正文不要用 mono</DSRule>
    </>
  );
}

export function DSPI18n() {
  return (
    <>
      <DSHeader title="i18n · 中英必填，其他可加" sub="i18n 是设计契约的一部分，不是后端的事" />
      <PrincipleCard
        name="为什么"
        desc="Rivmoon 矩阵服务 14 种欧洲语言。后台数据如不强制中英 + 其他，会出现「DE 用户读到英文标题」的体验。"
      />
      <PrincipleCard
        name="规则"
        examples={[
          '中文 + 英文必填',
          '其他语言通过 + 按钮手动添加',
          '每行内嵌「从英文翻译」按钮（mock）',
          '客户端按用户最近使用语言展示',
          '未维护的语言 fallback 到英文',
          '用户最近使用语言来自最近一次阅读会话',
        ]}
      />
      <PrincipleCard
        name="覆盖范围"
        examples={[
          '分类名 / 标签名',
          '推荐区块标题 / Banner 标题',
          '推送模板（标题 + 正文）',
          'FAQ（问题 + 答案）',
          '书籍下架原因（全量 + per-App）',
          '财务 BONUS 发放原因',
        ]}
      />
      <DSRule tone="do">
        用 <code>MultiLangFormRows</code> 实现，避免重复造轮子
      </DSRule>
    </>
  );
}

export function DSPStatus() {
  return (
    <>
      <DSHeader title="状态色三层" sub="一个状态有三种视觉强度，按上下文选择" />
      <PrincipleCard
        name="第一层 · chip"
        desc="紧凑显示在表格 / 卡片 / 标题旁，2px 圆点 + 文字。1cm 内的状态展示用这个"
      />
      <PrincipleCard
        name="第二层 · 行背景"
        desc="App 级覆盖、未跟随全量、需 attention 的行 — 背景换为 status-soft，让整行高亮"
      />
      <PrincipleCard
        name="第三层 · 大状态卡"
        desc="详情页顶部、状态机选择卡 — 用大 icon + 状态名 + 描述，明确告知当前态"
      />
      <DSRule tone="do">同一状态在同一页面只用一种强度</DSRule>
      <DSRule tone="dont">不要在 chip 旁再加一个同义文字 — 已经说过了</DSRule>
    </>
  );
}

export function DSPFallback() {
  return (
    <>
      <DSHeader title="fallback 兜底链" sub="数据缺失时的优雅降级" />
      <PrincipleCard
        name="多语言 fallback"
        examples={[
          '1. 请求语言 (例: DE)',
          '2. 该 App 允许语言中最近的一个',
          '3. 英文（永远存在 · 强制必填）',
        ]}
      />
      <PrincipleCard
        name="计价 fallback"
        examples={[
          '1. novels.cost_per_kw（per-novel 覆盖）',
          '2. novels.cost_default_per_kw（App 默认值）',
          '3. 硬编码常数 30 COIN（极端兜底）',
        ]}
      />
      <PrincipleCard
        name="时区 fallback"
        examples={[
          '1. users.day_cut_timezone（用户身份维度）',
          '2. IP 推断时区',
          '3. UTC+1（欧洲默认）',
        ]}
      />
      <DSRule tone="do">明确写出 fallback 链 — UI 上用 hint 文字告知</DSRule>
      <DSRule tone="dont">不要静默 fallback — 用户应该知道当前显示的值来自哪一层</DSRule>
    </>
  );
}
